import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX } from "react-icons/hi";
import { isGhostReasonValid } from "./ghostSession.utils";

const GhostStartModal = ({
  open,
  onClose,
  title,
  description,
  targetLabel,
  confirmLabel = "Start ghost session",
  submitting = false,
  onSubmit,
  allowReadOnly = false,
  affiliations = null,
}) => {
  const [reason, setReason] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [selectedSpId, setSelectedSpId] = useState(null);
  const [error, setError] = useState("");

  const affiliationOptions = Array.isArray(affiliations) ? affiliations : [];

  useEffect(() => {
    if (!open) {
      return;
    }

    if (Array.isArray(affiliations) && affiliations.length === 1) {
      setSelectedSpId(affiliations[0].sp_id);
      return;
    }

    setSelectedSpId(null);
  }, [open, affiliations]);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    setReason("");
    setReadOnly(false);
    setSelectedSpId(null);
    setError("");
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isGhostReasonValid(reason)) {
      setError(`Reason must be at least 10 characters.`);
      return;
    }

    if (affiliationOptions.length > 1 && selectedSpId == null) {
      setError("Select a tenant affiliation.");
      return;
    }

    setError("");
    await onSubmit({
      reason: reason.trim(),
      readOnly,
      spId: selectedSpId ?? affiliationOptions[0]?.sp_id ?? null,
    });
    setReason("");
    setReadOnly(false);
    setSelectedSpId(null);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[10002]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/50" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10002] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                    {description ? (
                      <Dialog.Description className="mt-1 text-sm text-gray-500">
                        {description}
                      </Dialog.Description>
                    ) : null}
                    {targetLabel ? (
                      <p className="mt-3 text-sm text-gray-700">
                        Target:{" "}
                        <span className="font-medium text-gray-900">{targetLabel}</span>
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <HiX className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                  {affiliationOptions.length > 1 ? (
                    <div>
                      <label
                        htmlFor="ghost-start-tenant"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Tenant affiliation
                      </label>
                      <select
                        id="ghost-start-tenant"
                        value={selectedSpId ?? ""}
                        onChange={(event) =>
                          setSelectedSpId(
                            event.target.value ? Number(event.target.value) : null
                          )
                        }
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        <option value="">Select tenant…</option>
                        {affiliationOptions.map((affiliation) => (
                          <option key={affiliation.sp_id} value={affiliation.sp_id}>
                            {affiliation.sp_name || `Tenant ${affiliation.sp_id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  <div>
                    <label
                      htmlFor="ghost-start-reason"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Reason
                    </label>
                    <textarea
                      id="ghost-start-reason"
                      rows={4}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      placeholder="Support ticket reference and purpose (min 10 characters)"
                    />
                    {error ? (
                      <p className="mt-1 text-sm text-red-600">{error}</p>
                    ) : null}
                  </div>

                  {allowReadOnly ? (
                    <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={readOnly}
                        onChange={(event) => setReadOnly(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>
                        Read-only ghost mode
                        <span className="mt-1 block text-xs text-gray-500">
                          Allows GET requests only — safe for demos and training.
                        </span>
                      </span>
                    </label>
                  ) : null}

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                    >
                      {submitting ? "Starting…" : confirmLabel}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GhostStartModal;
